import React, { useState, useEffect } from "react";
import { Checkbox, FormControlLabel, FormGroup, Box, Alert } from "@mui/material";
import { NotificationType } from "../../data/Enums";

interface Props {
  onChange: (selected: NotificationType[], notificationEnabled: boolean) => void;
}

const NotificationSettings: React.FC<Props> = ({ onChange }) => {
  const [isNotificationEnabled, setNotificationEnabled] = useState(true);
  const [selectedTypes, setSelectedTypes] = useState<NotificationType[]>([NotificationType.Email]);
  const [error, setError] = useState("");

  const isChecked = (type: NotificationType) => selectedTypes.includes(type);

  const handleNotificationToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setNotificationEnabled(checked);

    if (!checked) {
      setSelectedTypes([]);
      setError("");
    } else {
      setSelectedTypes([NotificationType.Email]);
    }
  };

  const handleTypeToggle =
    (type: NotificationType) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;

      setSelectedTypes((prev) => {
        const updated = checked ? [...prev, type] : prev.filter((t) => t !== type);
        return updated;
      });
    };

  useEffect(() => {
    onChange(selectedTypes, isNotificationEnabled);
  }, [isNotificationEnabled, selectedTypes, onChange]);

  useEffect(() => {
    if (isNotificationEnabled && selectedTypes.length === 0) {
      setError("At least one notification type should be selected.");
    } else {
      setError("");
    }
  }, [isNotificationEnabled, selectedTypes]);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: "2px" }}>
          {error}
        </Alert>
      )}
      <FormGroup row>
        <FormControlLabel
          control={<Checkbox checked={isNotificationEnabled} onChange={handleNotificationToggle} />}
          label="Notification"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isChecked(NotificationType.Email)}
              onChange={handleTypeToggle(NotificationType.Email)}
              disabled={!isNotificationEnabled}
            />
          }
          label="Email"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isChecked(NotificationType.Sms)}
              onChange={handleTypeToggle(NotificationType.Sms)}
              disabled={!isNotificationEnabled}
            />
          }
          label="SMS"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isChecked(NotificationType.InApp)}
              onChange={handleTypeToggle(NotificationType.InApp)}
              disabled={!isNotificationEnabled}
            />
          }
          label="InApp"
        />
      </FormGroup>
    </Box>
  );
};

export default NotificationSettings;
